import { useState } from "react";
import { IconBan } from "@tabler/icons-react";
import { Badge, EditRowActions, Input, NumberInput, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { IndicadorImpuesto } from "@/lib/types";
import { useCambiarEstadoIndicadorImpuesto, useEditarIndicadorImpuesto } from "../hooks/use-indicadores-impuesto-mutations";

/**
 * Fila con toggle de edición (código/tasa/descripción, RN-16 exige que la
 * tasa venga siempre del catálogo) · baja lógica en vez de eliminar
 * (RN-15/D-9: el código puede estar referenciado como snapshot en
 * renglones históricos de prorrateo y póliza).
 */
export function IndicadorRow({ indicador }: { indicador: IndicadorImpuesto }) {
  const [editando, setEditando] = useState(false);
  const [codigo, setCodigo] = useState(indicador.codigo);
  const [tasaPct, setTasaPct] = useState<number>(indicador.tasaOCuota * 100);
  const [descripcion, setDescripcion] = useState(indicador.descripcion);
  const confirm = useConfirmDialog();

  const editar = useEditarIndicadorImpuesto({ onEditado: () => setEditando(false) });
  const cambiarEstado = useCambiarEstadoIndicadorImpuesto();

  const confirmarBaja = async () => {
    const ok = await confirm({
      title: `Dar de baja "${indicador.codigo}"`,
      message:
        "Deja de ofrecerse en los selectores de captura del prorrateo. Los renglones históricos que ya usan este código conservan su indicador (RN-15). Puedes reactivarlo después.",
      confirmLabel: "Dar de baja",
      confirmColor: "peligro",
      icon: <IconBan size={14} />,
    });
    if (ok) cambiarEstado.mutate({ id: indicador.id, codigo: indicador.codigo, activo: false });
  };

  const cancelarEdicion = () => {
    setCodigo(indicador.codigo);
    setTasaPct(indicador.tasaOCuota * 100);
    setDescripcion(indicador.descripcion);
    setEditando(false);
  };

  const guardar = () =>
    editar.mutate({
      id: indicador.id,
      input: { codigo: codigo.trim().toUpperCase(), tasaOCuota: tasaPct / 100, descripcion: descripcion.trim() },
    });

  if (editando) {
    return (
      <Tr className="bg-muted/50">
        <Td>
          <Input size="xs" value={codigo} onChange={(e) => setCodigo(e.currentTarget.value.toUpperCase())} maxLength={4} className="w-20 font-mono" autoFocus />
        </Td>
        <Td>
          <NumberInput size="xs" value={tasaPct} onChange={(v) => setTasaPct(typeof v === "number" ? v : 0)} min={0} max={100} step={0.5} decimals={4} suffix="%" className="w-25" />
        </Td>
        <Td>
          <Input size="xs" value={descripcion} onChange={(e) => setDescripcion(e.currentTarget.value)} />
        </Td>
        <Td>
          <BadgeEstado activo={indicador.activo} />
        </Td>
        <Td align="right">
          <EditRowActions
            mode="edit"
            saving={editar.isPending}
            onCancel={cancelarEdicion}
            onSave={guardar}
          />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr>
      <Td>
        <Text fw={600} size="sm" ff="mono">
          {indicador.codigo}
        </Text>
      </Td>
      <Td>
        <Text size="sm" ff="mono">
          {(indicador.tasaOCuota * 100).toFixed((indicador.tasaOCuota * 100) % 1 === 0 ? 0 : 2)}%
        </Text>
      </Td>
      <Td>
        <Text size="sm">{indicador.descripcion}</Text>
      </Td>
      <Td>
        <BadgeEstado activo={indicador.activo} />
      </Td>
      <Td align="right">
        <EditRowActions
          mode="view"
          onEdit={() => setEditando(true)}
          onDelete={indicador.activo ? confirmarBaja : undefined}
          deleteVariant="baja"
          deleting={cambiarEstado.isPending}
          reactivable={!indicador.activo}
          onReactivar={() => cambiarEstado.mutate({ id: indicador.id, codigo: indicador.codigo, activo: true })}
          reactivando={cambiarEstado.isPending}
        />
      </Td>
    </Tr>
  );
}

function BadgeEstado({ activo }: { activo: boolean }) {
  return (
    <Badge size="sm" color={activo ? "exito" : "slate"}>
      {activo ? "Activo" : "Inactivo"}
    </Badge>
  );
}
